import { Controller } from '@hotwired/stimulus'

const FILTERS = [
  { key: 'changes', label: 'With changes' },
  { key: 'unchanged', label: 'Unchanged' },
  { key: 'skipped', label: 'Skipped' }
]

const PILLS = {
  changes: { ready: 'Ready', applied: 'Applied' },
  unchanged: { ready: 'Unchanged', applied: 'Unchanged' },
  skipped: { ready: 'Skipped', applied: 'Skipped' }
}

const identity = (mapping) => {
  const stop = mapping.stop_external_id || `legacy code ${mapping.legacy_stop_code}`
  return `${mapping.feed_key} / ${stop}`
}

const hubLabel = (hub) => `${hub.hub_type || 'unknown'} hub ${hub.hub_external_id || 'unknown'}`

// Which view a hub belongs in. A hub that is ready (or applied) but changes
// nothing is "unchanged"; the server still processes it, as a no-op.
const categoryOf = (hub) => {
  if (hub.status === 'skipped') return 'skipped'
  const changes = hub.changes || {}
  const changed = (changes.add || []).length + (changes.remove || []).length + (changes.groups || []).length
  return changed > 0 ? 'changes' : 'unchanged'
}

export default class extends Controller {
  static targets = [
    'form', 'file', 'fileName', 'drop', 'status', 'previewButton', 'results', 'summary', 'chips', 'list',
    'empty', 'confirmBar', 'confirmText', 'confirmButton', 'rowTemplate', 'sectionTemplate', 'groupTemplate'
  ]

  static values = {
    previewUrl: String,
    confirmUrl: String,
    hubUrl: String
  }

  connect () {
    this.hubs = []
    this.filter = 'changes'
    this.previewToken = null
  }

  allowDrop (event) {
    event.preventDefault()
    this.dropTarget.classList.add('is-dragging')
  }

  leaveDrop () {
    this.dropTarget.classList.remove('is-dragging')
  }

  drop (event) {
    event.preventDefault()
    this.leaveDrop()
    const files = event.dataTransfer.files
    if (files.length !== 1 || !/\.csv$/i.test(files[0].name)) {
      this.setStatus('Choose a single .csv file.', 'error')
      return
    }

    const transfer = new window.DataTransfer()
    transfer.items.add(files[0])
    this.fileTarget.files = transfer.files
    this.fileChosen()
  }

  // A different file invalidates whatever was previewed.
  fileChosen () {
    const file = this.fileTarget.files[0]
    this.fileNameTarget.textContent = file ? file.name : 'No file chosen'
    this.reset()
  }

  async preview (event) {
    event.preventDefault()
    this.reset()
    this.previewButtonTarget.disabled = true
    this.setStatus('Reading the snapshot…')

    try {
      const response = await fetch(this.previewUrlValue, this.requestOptions(new window.FormData(this.formTarget)))
      const body = await response.json()
      if (!response.ok) throw new Error(body.error || 'Could not preview the mapping snapshot.')

      this.previewToken = body.preview_token
      this.hubs = body.hubs || []
      this.filter = this.defaultFilter()
      this.render()
      const count = this.readyChangedCount()
      this.setStatus(count > 0
        ? 'Preview ready. Nothing has been changed yet.'
        : 'Preview ready. No hub in this file would change.')
    } catch (error) {
      this.setStatus(error.message || 'Could not preview the mapping snapshot.', 'error')
    } finally {
      this.previewButtonTarget.disabled = false
    }
  }

  async confirm () {
    if (!this.previewToken || !this.fileTarget.files.length) return

    const data = new window.FormData(this.formTarget)
    data.append('preview_token', this.previewToken)
    this.confirmButtonTarget.disabled = true

    try {
      const response = await fetch(this.confirmUrlValue, this.requestOptions(data))
      const body = await response.json()
      if (!response.ok) {
        this.previewToken = null
        this.confirmBarTarget.hidden = true
        throw new Error(body.repreview_required
          ? `${body.error} Preview the file again.`
          : (body.error || 'Could not import the mapping snapshot. Preview the file again.'))
      }

      const applied = (body.hubs || []).filter((hub) => hub.status === 'applied' && categoryOf(hub) === 'changes').length
      this.previewToken = null
      this.hubs = body.hubs || []
      this.render()
      this.formTarget.reset()
      this.fileNameTarget.textContent = 'No file chosen'
      this.setStatus(`Import complete. Mappings were replaced for ${applied} hub${applied === 1 ? '' : 's'}; all other hubs were left unchanged.`, 'success')
    } catch (error) {
      this.setStatus(error.message || 'Could not import the mapping snapshot.', 'error')
    } finally {
      this.confirmButtonTarget.disabled = false
    }
  }

  selectFilter (event) {
    this.filter = event.currentTarget.dataset.filter
    this.renderChips()
    this.renderList()
  }

  // ---- rendering ----

  render () {
    this.resultsTarget.hidden = false
    this.renderSummary()
    this.renderChips()
    this.renderList()
    this.renderConfirmBar()
  }

  counts () {
    const counts = { changes: 0, unchanged: 0, skipped: 0, added: 0, removed: 0 }
    this.hubs.forEach((hub) => {
      counts[categoryOf(hub)] += 1
      counts.added += ((hub.changes || {}).add || []).length
      counts.removed += ((hub.changes || {}).remove || []).length
    })
    return counts
  }

  defaultFilter () {
    const counts = this.counts()
    return FILTERS.find((filter) => counts[filter.key] > 0)?.key || 'changes'
  }

  readyChangedCount () {
    return this.hubs.filter((hub) => hub.status === 'ready' && categoryOf(hub) === 'changes').length
  }

  renderSummary () {
    const counts = this.counts()
    const tiles = [
      [counts.changes, 'hubs with changes'],
      [counts.unchanged, 'hubs unchanged'],
      [counts.skipped, 'hubs skipped'],
      [`+${counts.added}`, 'stops added'],
      [`−${counts.removed}`, 'stops removed']
    ]
    this.summaryTarget.replaceChildren(...tiles.map(([value, label]) => {
      const tile = document.createElement('div')
      tile.className = 'imh-transfer-tile'
      const number = document.createElement('div')
      number.className = 'imh-transfer-tile-value'
      number.textContent = value
      const caption = document.createElement('div')
      caption.className = 'imh-transfer-tile-label'
      caption.textContent = label
      tile.append(number, caption)
      return tile
    }))
  }

  renderChips () {
    const counts = this.counts()
    this.chipsTarget.replaceChildren(...FILTERS.map(({ key, label }) => {
      const chip = document.createElement('button')
      chip.type = 'button'
      chip.className = 'imh-transfer-chip'
      chip.dataset.filter = key
      chip.dataset.action = 'click->imh-snapshot-import#selectFilter'
      chip.setAttribute('aria-pressed', String(this.filter === key))
      chip.classList.toggle('is-active', this.filter === key)
      chip.textContent = `${label} (${counts[key]})`
      return chip
    }))
  }

  renderList () {
    const visible = this.hubs.filter((hub) => categoryOf(hub) === this.filter)
    this.listTarget.replaceChildren(...visible.map((hub) => this.buildRow(hub)))
    this.emptyTarget.hidden = visible.length > 0
  }

  renderConfirmBar () {
    const count = this.readyChangedCount()
    this.confirmBarTarget.hidden = !this.previewToken || count === 0
    this.confirmTextTarget.textContent = `Replace the stop mappings of ${count} hub${count === 1 ? '' : 's'}.`
  }

  buildRow (hub) {
    const row = this.rowTemplateTarget.content.firstElementChild.cloneNode(true)
    const category = categoryOf(hub)
    const slot = (name) => row.querySelector(`[data-slot="${name}"]`)
    const changes = hub.changes || {}

    slot('dot').classList.add(`is-${category}`)
    slot('type').textContent = hub.hub_name ? hubLabel(hub) : ''
    slot('pill').textContent = PILLS[category][hub.status === 'applied' ? 'applied' : 'ready']
    slot('pill').classList.add(`is-${category === 'changes' && hub.status === 'applied' ? 'applied' : category}`)

    const name = slot('name')
    if (hub.hub_id) {
      const link = document.createElement('a')
      link.href = this.hubUrlValue.replace(':id', hub.hub_id)
      link.textContent = hub.hub_name || hubLabel(hub)
      link.className = 'hover:underline text-primary'
      name.appendChild(link)
    } else {
      name.textContent = hub.hub_name || hubLabel(hub)
    }

    const added = (changes.add || []).length
    const removed = (changes.remove || []).length
    if (added || removed) {
      slot('counts').textContent = `+${added} / −${removed}`
    }

    const sections = this.detailSections(hub)
    const details = slot('details')
    if (sections.length === 0) {
      row.querySelector('details').classList.add('is-empty')
    } else {
      details.replaceChildren(...sections)
    }
    return row
  }

  detailSections (hub) {
    const changes = hub.changes || {}
    const sections = []
    const add = (title, items, prose) => { if (items.length) sections.push(this.buildSection(title, items, prose)) }

    add('Reasons it is skipped', hub.reasons || [], true)
    add('Stops added', (changes.add || []).map(identity))
    add('Stops removed', (changes.remove || []).map(identity))
    add('Unresolved exact stop IDs', (changes.unresolved_mappings || []).map(identity))
    add('Code-only mappings kept', (changes.retain_code_only || []).map((mapping) => {
      const group = mapping.group_key ? ` → ${mapping.group_key} (${mapping.group_name})` : ' → standalone'
      return `${mapping.feed_key} / ${mapping.legacy_stop_code}${group}`
    }))
    ;(changes.groups || []).forEach((group) => sections.push(this.buildGroup(group)))
    return sections
  }

  buildSection (title, items, prose = false) {
    const section = this.sectionTemplateTarget.content.firstElementChild.cloneNode(true)
    section.classList.toggle('is-prose', prose)
    section.querySelector('[data-slot="title"]').textContent = title
    section.querySelector('[data-slot="items"]').replaceChildren(...items.map((text) => {
      const item = document.createElement('li')
      item.textContent = text
      return item
    }))
    return section
  }

  buildGroup (group) {
    const block = this.groupTemplateTarget.content.firstElementChild.cloneNode(true)
    const none = (value) => value || 'none'
    const list = (members) => (members || []).length ? members.map(identity).join(', ') : 'none'
    const anchor = (mapping) => mapping ? identity(mapping) : 'none'

    block.querySelector('[data-slot="title"]').textContent = `Group ${group.group_key}`
    const facts = [
      ['Name', `${none(group.name_before)} → ${none(group.name_after)}`],
      ['Stops added', list(group.members_added)],
      ['Stops removed', list(group.members_removed)],
      ['Anchor', `${anchor(group.anchor_before)} → ${anchor(group.anchor_after)}`]
    ]
    block.querySelector('[data-slot="facts"]').replaceChildren(...facts.flatMap(([term, value]) => {
      const dt = document.createElement('dt')
      dt.textContent = term
      const dd = document.createElement('dd')
      dd.textContent = value
      return [dt, dd]
    }))
    return block
  }

  // ---- helpers ----

  reset () {
    this.previewToken = null
    this.hubs = []
    this.resultsTarget.hidden = true
    this.confirmBarTarget.hidden = true
    this.setStatus('')
  }

  requestOptions (body) {
    return {
      method: 'POST',
      body,
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
      }
    }
  }

  setStatus (message, tone) {
    this.statusTarget.textContent = message
    this.statusTarget.className = `imh-transfer-status${tone ? ` is-${tone}` : ''}`
  }
}
